import React, { useMemo } from "react";
import CertificateElementView from "./CertificateElement";
import {
  CertificateElement,
  CertificatePageSettings,
  getPageSize,
  ReportChartPreview,
  ReportChecklistPreview,
  ReportTable,
} from "./certificateTypes";
import { applyLabelValueStyle, resolvePlaceholders } from "./certificatePlaceholders";

type Interaction =
  | { kind: "move"; id: string; startX: number; startY: number; origX: number; origY: number }
  | { kind: "resize"; id: string; startX: number; startY: number; origW: number; origH: number };

type CertificateCanvasProps = {
  page: CertificatePageSettings;
  elements: CertificateElement[];
  selectedId: string | null;
  previewData: Record<string, string>;
  previewTables?: Record<string, ReportTable>;
  previewCharts?: Record<string, ReportChartPreview>;
  previewChecklists?: Record<string, ReportChecklistPreview>;
  interactive: boolean;
  zoom: number;
  pageCount?: number;
  paperRef: React.RefObject<HTMLDivElement>;
  onSelect?: (id: string | null) => void;
  onElementsChange?: (updater: (elements: CertificateElement[]) => CertificateElement[]) => void;
};

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function CertificateCanvas({
  page,
  elements,
  selectedId,
  previewData,
  previewTables,
  previewCharts,
  previewChecklists,
  interactive,
  zoom,
  pageCount,
  paperRef,
  onSelect,
  onElementsChange,
}: CertificateCanvasProps) {
  const size = getPageSize(page.orientation, page.size);
  const pages = Math.max(
    1,
    pageCount || 1,
    ...elements.map((el) => Math.floor(Number(el.pageIndex) || 0) + 1)
  );
  const interaction = React.useRef<Interaction | null>(null);
  const onElementsChangeRef = React.useRef(onElementsChange);
  onElementsChangeRef.current = onElementsChange;

  const pxPerMm = React.useCallback(() => {
    const rect = paperRef.current?.getBoundingClientRect();
    if (!rect?.width) return 96 / 25.4;
    return rect.width / size.widthMm;
  }, [paperRef, size.widthMm]);

  const onPointerMove = React.useCallback(
    (event: PointerEvent) => {
      const current = interaction.current;
      if (!current) return;
      const scale = pxPerMm();
      const dx = (event.clientX - current.startX) / scale;
      const dy = (event.clientY - current.startY) / scale;
      const patch =
        current.kind === "move"
          ? {
              x: clamp(current.origX + dx, 0, size.widthMm - 8),
              y: clamp(current.origY + dy, 0, size.heightMm - 8),
            }
          : {
              width: clamp(current.origW + dx, 8, size.widthMm),
              height: clamp(current.origH + dy, 6, size.heightMm),
            };
      onElementsChangeRef.current?.((list) =>
        list.map((item) => (item.id === current.id ? { ...item, ...patch } : item))
      );
    },
    [pxPerMm, size.heightMm, size.widthMm]
  );

  const endInteraction = React.useCallback(() => {
    interaction.current = null;
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", endInteraction);
  }, [onPointerMove]);

  const startMove = (event: React.PointerEvent, id: string) => {
    const el = elements.find((item) => item.id === id);
    if (!el) return;
    interaction.current = {
      kind: "move",
      id,
      startX: event.clientX,
      startY: event.clientY,
      origX: el.x,
      origY: el.y,
    };
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", endInteraction);
  };

  const startResize = (event: React.PointerEvent, id: string) => {
    const el = elements.find((item) => item.id === id);
    if (!el) return;
    interaction.current = {
      kind: "resize",
      id,
      startX: event.clientX,
      startY: event.clientY,
      origW: el.width,
      origH: el.height,
    };
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", endInteraction);
  };

  const rendered = useMemo(
    () =>
      elements.map((element) => {
        const spacing =
          element.section === "body" ? page.fieldSpacing : 0;
        const paragraph =
          element.section === "body" ? page.paragraphSpacing : 0;
        const resolved = applyLabelValueStyle(
          resolvePlaceholders(element.content, previewData),
          page.labelValueStyle
        );
        return { element, spacing, paragraph, resolved };
      }),
    [elements, page.fieldSpacing, page.labelValueStyle, page.paragraphSpacing, previewData]
  );

  return (
    <div
      className="certificate-zoom-frame"
      style={{
        width: `calc(${size.widthMm}mm * ${zoom})`,
        height: `calc((${size.heightMm}mm * ${pages} + ${pages > 1 ? 8 * (pages - 1) : 0}mm) * ${zoom})`,
      }}
    >
      <div className="certificate-zoom" style={{ transform: `scale(${zoom})` }}>
      <div
        id="certificate-print-sheet"
        ref={paperRef}
        className="certificate-print-stack"
        style={{
          ["--certificate-font-family" as string]: page.fontFamily,
          fontFamily: page.fontFamily,
          fontSize: `${page.fontSize}pt`,
          fontWeight: page.fontWeight,
          fontStyle: page.italic ? "italic" : "normal",
          textDecoration: page.underline ? "underline" : "none",
          lineHeight: page.lineHeight,
          textAlign: page.textAlign,
        }}
        onPointerDown={() => interactive && onSelect?.(null)}
      >
        {Array.from({ length: pages }, (_, pageIndex) => (
          <div
            key={pageIndex}
            className="certificate-paper"
            data-report-page={pageIndex}
            style={{
              width: `${size.widthMm}mm`,
              height: `${size.heightMm}mm`,
            }}
          >
            {page.showSafeArea && interactive ? (
              <div
                className="certificate-safe-area"
                style={{
                  left: `${page.marginMm}mm`,
                  top: `${page.marginMm}mm`,
                  width: `${size.widthMm - page.marginMm * 2}mm`,
                  height: `${size.heightMm - page.marginMm * 2}mm`,
                }}
              />
            ) : null}
            {rendered
              .filter(
                ({ element }) =>
                  Math.floor(Number(element.pageIndex) || 0) === pageIndex
              )
              .map(({ element, spacing, paragraph, resolved }) => {
                const isTable =
                  element.type === "table" ||
                  Boolean(element.fieldKey && previewTables?.[element.fieldKey]);
                return (
                <CertificateElementView
                  key={element.id}
                  element={{
                    ...element,
                    height: isTable
                      ? element.height
                      : element.height + spacing * 0.15,
                    // Tables must keep a neutral line-height or PDF text drifts
                    lineHeight: isTable
                      ? 1
                      : element.lineHeight + paragraph * 0.02,
                  }}
                  selected={selectedId === element.id}
                  interactive={interactive}
                  content={resolved}
                  table={
                    element.fieldKey ? previewTables?.[element.fieldKey] : undefined
                  }
                  chart={
                    element.fieldKey ? previewCharts?.[element.fieldKey] : undefined
                  }
                  checklist={
                    element.fieldKey
                      ? previewChecklists?.[element.fieldKey]
                      : undefined
                  }
                  inheritedFontFamily={page.fontFamily}
                  logoSrc={
                    element.id === "letterhead" || element.fieldKey === "letterhead"
                      ? page.letterheadSrc || page.logoSrc
                      : page.logoSrc
                  }
                  onSelect={(id) => onSelect?.(id)}
                  onMoveStart={startMove}
                  onResizeStart={startResize}
                />
                );
              })}
          </div>
        ))}
      </div>
      </div>
    </div>
  );
}

export default CertificateCanvas;
